/**
 * crmFunnel/mockData.ts
 *
 * Realistic mock Close CRM data for Meta paid leads.
 * Covers Jan 2 – Jan 23 2026 (22 days).
 *
 * All rows represent leads sourced from Meta paid ads:
 *   platform: "meta", utmMedium: "paid_social" or source: "meta_ads"/"paid_meta"
 *
 * Funnel ratios in mock data (realistic for paid social → high-ticket):
 *   MQL rate:     ~45%
 *   Booking rate: ~55% of MQLs
 *   Show rate:    ~70% of bookings
 *   Close rate:   ~25% of shows
 *   Revenue:      $2,000 – $5,000 per deal
 */

import type { CrmLeadRow } from "./types";

// ─────────────────────────────────────────────
// SEED DATA HELPERS
// ─────────────────────────────────────────────

const CAMPAIGNS = [
  { id: "camp_001", name: "Brand Awareness - Jan 2026" },
  { id: "camp_002", name: "Lead Gen - Cold Audience" },
  { id: "camp_003", name: "Retargeting - Website Visitors" },
];

const ADSETS = [
  { id: "adset_001", name: "LA - Women 25-44", campaignId: "camp_001" },
  { id: "adset_002", name: "NY - Men 30-50",   campaignId: "camp_001" },
  { id: "adset_003", name: "Lookalike 1% - Buyers", campaignId: "camp_002" },
  { id: "adset_004", name: "Interest - Fitness",    campaignId: "camp_002" },
  { id: "adset_005", name: "Retarget - Video 75%",  campaignId: "camp_003" },
];

const ADS = [
  { id: "ad_001", name: "Video - Testimonial v1", adsetId: "adset_001" },
  { id: "ad_002", name: "Image - Before/After",   adsetId: "adset_002" },
  { id: "ad_003", name: "Carousel - Results",     adsetId: "adset_003" },
  { id: "ad_004", name: "Video - Hook v2",        adsetId: "adset_004" },
  { id: "ad_005", name: "Image - Offer Expired",  adsetId: "adset_005" },
];

const SOURCES = ["paid_meta", "meta_ads"] as const;
const COUNTRIES = ["US", "CA", "GB", "AU"];
const FIRST_NAMES = ["James", "Emily", "Carlos", "Sarah", "Michael", "Jessica", "David", "Ashley", "Daniel", "Megan"];
const LAST_NAMES  = ["Smith", "Johnson", "Williams", "Brown", "Jones", "Garcia", "Miller", "Davis", "Wilson", "Taylor"];

function pick<T>(arr: T[], seed: number): T {
  return arr[seed % arr.length] as T;
}

function buildLead(
  index: number,
  date: string,
  isMql: boolean,
  meetingsBooked: number,
  meetingsAttended: number,
  isClosedWon: boolean,
  revenue: number,
  adIndex: number,
): CrmLeadRow {
  const ad      = ADS[adIndex % ADS.length]!;
  const adset   = ADSETS.find(a => a.id === ad.adsetId) ?? ADSETS[0]!;
  const campaign = CAMPAIGNS.find(c => c.id === adset.campaignId) ?? CAMPAIGNS[0]!;

  return {
    leadId:           `lead_${String(index + 1).padStart(4, "0")}`,
    createdAt:        date,
    platform:         "meta",
    source:           pick(SOURCES, index),
    utmMedium:        "paid_social",
    utmSource:        index % 3 === 0 ? "instagram" : "facebook",
    utmCampaign:      campaign.name,
    utmContent:       ad.name,
    utmTerm:          index % 2 === 0 ? "fitness_audience" : "lookalike_buyers",
    metaCampaignId:   campaign.id,
    metaCampaignName: campaign.name,
    metaAdsetId:      adset.id,
    metaAdsetName:    adset.name,
    metaAdId:         ad.id,
    metaAdName:       ad.name,
    firstName:        pick(FIRST_NAMES, index),
    lastName:         pick(LAST_NAMES, index + 3),
    email:            `lead${index + 1}@example.com`,
    phone:            `+1555${String(1000 + index).padStart(4, "0")}`,
    country:          pick(COUNTRIES, index),
    isMql,
    meetingsBooked,
    meetingsAttended,
    isClosedWon,
    revenue,
  };
}

// ─────────────────────────────────────────────
// GENERATE MOCK ROWS
// ─────────────────────────────────────────────

function generateMockRows(): CrmLeadRow[] {
  const rows: CrmLeadRow[] = [];
  let index = 0;

  // Generate dates for the last 90 days relative to today so data is always in range
  const today = new Date();
  const dateLeadCounts: Array<{ date: string; count: number }> = [];
  for (let i = 89; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().slice(0, 10);
    const dayOfWeek = d.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const count = isWeekend ? 3 + (i % 4) : 5 + (i % 7);
    dateLeadCounts.push({ date: dateStr, count });
  }

  for (const { date, count } of dateLeadCounts) {
    for (let i = 0; i < count; i++) {
      const isMql           = (index % 10) < 4;   // ~40% MQL rate
      const meetingsBooked  = isMql && (index % 5) < 3 ? 1 : 0;  // ~60% of MQLs book
      const meetingsAttended = meetingsBooked && (index % 7) < 5 ? 1 : 0;  // ~71% show
      const isClosedWon     = meetingsAttended === 1 && (index % 4) === 0;  // ~25% close
      const revenue         = isClosedWon ? [2500, 3000, 3500, 4000, 5000][index % 5]! : 0;

      rows.push(buildLead(index, date, isMql, meetingsBooked, meetingsAttended, isClosedWon, revenue, i));
      index++;
    }
  }

  return rows;
}

export const RAW_CRM_FUNNEL_ROWS: CrmLeadRow[] = generateMockRows();
